import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSavePost, useVotePoll, useDeletePost } from '../../hooks/usePosts';
import MediaGallery from './MediaGallery';
import { apiService, type Post } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { usePermissions } from '../../hooks/usePermissions';
import ConfirmModal from '../UI/ConfirmModal';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp, buttonHover, buttonTap, scaleIn, backdropFade } from '../../utils/animations';

// Allowed categories - centralized list (kept for future use)
// const ALLOWED_CATEGORIES = ['hip', 'knee', 'ankle'];

interface PostCardProps {
  post: Post;
  isSearchResult?: boolean;
  searchQuery?: string;
}

// Helper function to highlight search terms in text
// Supports partial word matching (e.g., searching "version" will highlight "retroversion" and "retroverted")
const highlightSearchTerm = (text: string, searchTerm?: string) => {
  if (!searchTerm || !text) return text;

  // Escape special regex characters but allow partial word matching
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Match the search term anywhere in a word (partial match)
  const regex = new RegExp(`(\\w*${escapedTerm}\\w*)`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Only highlight if this part actually contains the search term
    if (part && new RegExp(escapedTerm, 'gi').test(part)) {
      return (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
          {part}
        </mark>
      );
    }
    return part;
  });
};

export default function PostCard({ post, isSearchResult = false, searchQuery }: PostCardProps) {
  // Safety check: return null if post data is invalid
  if (!post || !post.id || !post.owner) {
    console.warn('PostCard: Invalid post data', post);
    return null;
  }

  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [videoPreviews, setVideoPreviews] = useState<string[]>([]);
  const [filePreviews, setFilePreviews] = useState<{name: string; type: string}[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCommentImage, setSelectedCommentImage] = useState<{ url: string; images: string[]; index: number } | null>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replyingToUsername, setReplyingToUsername] = useState<string>('');
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
  const [showCommentDeleteConfirm, setShowCommentDeleteConfirm] = useState<boolean>(false);
  const { user } = useAuth();
  const { showSuccess, showInfo } = useNotifications();
  const { canComment, isSurgeon } = usePermissions();

  // Check if current user is the poll creator
  const isPollCreator = post?.owner?.id.toString() === user?.id;

  // Helper function to get display name for post owner
  const getOwnerDisplayName = (owner: typeof post.owner) => {
    // If this post belongs to the current user, use their stored name
    if (user && owner.id.toString() === user.id) {
      return user.name || owner.name || owner.username || 'Unknown User';
    }
    // Otherwise use the owner's name or username from the post (prefer name)
    return owner.name || owner.username || 'Unknown User';
  };
  const savePost = useSavePost();
  const votePoll = useVotePoll();
  const deletePost = useDeletePost();
  const queryClient = useQueryClient();

  const handleSave = () => {
    const isSaving = !post.savedByUser;
    console.log('💾 Save button clicked:', {
      postId: post.id,
      currentlySaved: post.savedByUser,
      willSave: isSaving
    });
    
    savePost.mutate({ postId: post.id, isSaved: post.savedByUser }, {
      onSuccess: () => {
        console.log('✅ Save API success');
        showSuccess(
          isSaving ? 'Post Saved!' : 'Post Removed',
          isSaving ? 'Post added to your saved posts' : 'Post removed from saved posts'
        );
      },
      onError: (error) => {
        console.error('❌ Save API error:', error);
      }
    });
  };

  const { data: comments, isLoading: isLoadingComments, error, refetch: refetchComments } = useQuery({
    queryKey: ['comments', post.id],
    queryFn: () => apiService.getComments(post.id),
    enabled: showComments, // Only fetch comments when user expands the comments section
    retry: 2, // Retry failed requests up to 2 times
    staleTime: 0, // Always fetch fresh comments from server (no stale time)
    refetchOnMount: true, // Refetch when component mounts
  });

  const createCommentMutation = useMutation({
    mutationFn: ({ postId, comment, media, parentId }: { postId: number; comment: string; media?: { images?: File[], videos?: File[] }; parentId?: number }) =>
      apiService.createComment(postId, comment, media, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setNewComment('');
      setReplyText('');
      setReplyingTo(null);
      setSelectedImages([]);
      setSelectedVideos([]);
      setSelectedFiles([]);
      setImagePreviews([]);
      setVideoPreviews([]);
      setFilePreviews([]);
      showSuccess('Comment Added!', 'Your comment has been posted successfully');
    },
    onError: (error: Error) => {
      // Check if this is our graceful fallback error
      if (error.message.includes('media upload isn\'t supported yet')) {
        // Clear media but keep the text comment since it was posted
        setSelectedImages([]);
        setSelectedVideos([]);
        setSelectedFiles([]);
        setImagePreviews([]);
        setVideoPreviews([]);
        setFilePreviews([]);
        queryClient.invalidateQueries({ queryKey: ['comments', post.id] });
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        showInfo('Comment Posted', error.message);
      } else {
        console.error('Comment creation failed:', error);
        showInfo('Comment Failed', 'Could not post comment. Please try again.');
      }
    },
  });
  
  const upvoteCommentMutation = useMutation({
    mutationFn: (commentId: number) => {
      console.log(`Attempting to upvote comment ${commentId}...`);
      return apiService.upvoteComment(commentId);
    },
    onSuccess: () => {
      console.log('Upvote mutation succeeded, invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['comments', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      showSuccess('Comment Upvoted', 'Your upvote has been recorded');
    },
    onError: (error: any) => {
      console.error('Failed to upvote comment:', error);
      showInfo('Upvote Failed', 'Could not upvote comment. Please try again.');
    }
  });
  
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => {
      console.log(`Attempting to delete comment ${commentId}...`);
      return apiService.deleteComment(commentId);
    },
    onSuccess: () => {
      console.log('Delete mutation succeeded, invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['comments', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setCommentToDelete(null);
      setShowCommentDeleteConfirm(false);
      showSuccess('Comment Deleted', 'Comment has been successfully removed');
    },
    onError: (error: any) => {
      console.error('Failed to delete comment:', error);
      setCommentToDelete(null);
      setShowCommentDeleteConfirm(false);
      showInfo('Delete Failed', 'Could not delete comment. Please try again.');
    }
  });

  const handleComment = () => {
    setShowComments(!showComments);
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() || selectedImages.length > 0 || selectedVideos.length > 0) {
      const media = (selectedImages.length > 0 || selectedVideos.length > 0) ? {
        images: selectedImages.length > 0 ? selectedImages : undefined,
        videos: selectedVideos.length > 0 ? selectedVideos : undefined
      } : undefined;
      
      createCommentMutation.mutate({ 
        postId: post.id, 
        comment: newComment.trim(), 
        media 
      });
    }
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        setSelectedImages(prev => [...prev, file]);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreviews(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        setSelectedVideos(prev => [...prev, file]);
        const reader = new FileReader();
        reader.onload = (e) => {
          setVideoPreviews(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setSelectedFiles(prev => [...prev, file]);
        setFilePreviews(prev => [...prev, { name: file.name, type: 'pdf' }]);
      }
    });

    // Reset input
    e.target.value = '';
  };

  // Unused handlers - kept for potential future use
  // const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = Array.from(e.target.files || []);
  //   const imageFiles = files.filter(file => file.type.startsWith('image/'));

  //   setSelectedImages(prev => [...prev, ...imageFiles]);

  //   // Create previews
  //   imageFiles.forEach(file => {
  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       setImagePreviews(prev => [...prev, e.target?.result as string]);
  //     };
  //     reader.readAsDataURL(file);
  //   });
  // };

  // const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = Array.from(e.target.files || []);
  //   const videoFiles = files.filter(file => file.type.startsWith('video/'));

  //   setSelectedVideos(prev => [...prev, ...videoFiles]);

  //   // Create previews
  //   videoFiles.forEach(file => {
  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       setVideoPreviews(prev => [...prev, e.target?.result as string]);
  //     };
  //     reader.readAsDataURL(file);
  //   });
  // };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index: number) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index));
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleCommentUpvote = (commentId: number) => {
    console.log(`Comment upvote clicked for comment ID: ${commentId}`);
    upvoteCommentMutation.mutate(commentId, {
      onSuccess: () => {
        console.log(`Upvote for comment ${commentId} successful`);
      }
    });
  };

  const handleReplyToComment = (commentId: number, username: string) => {
    console.log(`Setting up reply to comment ${commentId} by ${username}`);
    setReplyingTo(commentId);
    setReplyingToUsername(username);
    setReplyText('');
    setReplyError(null);
  };
  
  const handleDeleteComment = (commentId: number) => {
    setCommentToDelete(commentId);
    setShowCommentDeleteConfirm(true);
  };
  
  const confirmDeleteComment = () => {
    if (commentToDelete) {
      deleteCommentMutation.mutate(commentToDelete);
    }
  };

  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setReplyError(null);
    
    if (!replyText.trim()) {
      setReplyError('Reply cannot be empty.');
      return;
    }
    
    if (!replyingTo) {
      setReplyError('No comment selected to reply to.');
      return;
    }
    
    console.log(`Submitting reply to comment ID ${replyingTo}: ${replyText}`);
    
    createCommentMutation.mutate({
      postId: post.id,
      comment: replyText.trim(),
      parentId: replyingTo
    });
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
    setReplyError(null);
    setReplyingToUsername('');
  };

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: `${post.title}\n\n${post.description.substring(0, 200)}${post.description.length > 200 ? '...' : ''}`,
      url: `${window.location.origin}/?post=${post.id}`,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        // Use native iOS/Android share sheet
        await navigator.share(shareData);
        console.log('✅ Native share successful');
      } else {
        // Fallback: copy link to clipboard
        await navigator.clipboard.writeText(shareData.url);
        showSuccess('Link Copied!', 'Post link copied to clipboard - share it anywhere');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        // User didn't cancel, so there was an actual error
        console.error('❌ Share failed:', error);
        try {
          await navigator.clipboard.writeText(shareData.url);
          showSuccess('Link Copied!', 'Post link copied to clipboard');
        } catch (clipboardError) {
          showInfo('Share Failed', 'Unable to share this post. Please try again.');
        }
      }
    }
    setShowMenu(false);
  };

  const handleReport = () => {
    showInfo('Report Submitted', 'Thank you for reporting this post. We will review it shortly.');
    setShowMenu(false);
  };

  const handleHidePost = () => {
    showInfo('Post Hidden', 'This post has been hidden from your feed');
    setShowMenu(false);
  };

  const handleDeleteConfirm = () => {
    console.log('🗑️ User confirmed delete for post:', post.id);
    deletePost.mutate(post.id, {
      onSuccess: () => {
        console.log('✅ Delete success - showing toast');
        setShowDeleteConfirm(false);
        // Show success toast with a slight delay to ensure modal is closed
        setTimeout(() => {
          console.log('🎉 Showing delete success toast');
          showSuccess('Post Deleted!', 'Your post has been permanently deleted');
        }, 100);
      },
      onError: (error) => {
        console.error('❌ Delete error:', error);
        setShowDeleteConfirm(false);
        // Show error toast with a slight delay
        setTimeout(() => {
          showInfo('Delete Failed', 'Could not delete the post. Please try again.');
        }, 100);
      }
    });
  };

  return (
    <motion.div
      id={`post-${post.id}`}
      className="theme-card mb-4 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 transition-all"
      style={{
        boxShadow: '0 2px 8px rgba(0, 60, 76, 0.08)'
      }}
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      whileHover={{
        y: -6,
        boxShadow: '0 12px 32px rgba(57, 181, 74, 0.15)',
        transition: { duration: 0.3 }
      }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="h-10 w-10 bg-healthy-green rounded-full flex items-center justify-center flex-shrink-0">
            {post.owner.profilePicture ? (
              <img
                src={post.owner.profilePicture}
                alt={getOwnerDisplayName(post.owner)}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-white text-sm font-medium select-none">
                {getOwnerDisplayName(post.owner).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          
          <div className="flex-1 min-w-0 pl-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-semibold theme-text">
                {getOwnerDisplayName(post.owner)}
              </h3>
              <span className="text-xs theme-text-muted">• {post.owner.country}</span>
            </div>
            <p className="text-xs theme-text-muted mt-1">
              {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
            </p>
          </div>

          {/* Joint Tags - moved to top right */}
          {post.tags && post.tags.length > 0 && (() => {
            console.log('🏷️ Post', post.id, 'tags:', post.tags);
            // Format and deduplicate tags
            const formatTagText = (text: string) => {
              // Remove all special characters including asterisks, extra spaces, etc.
              const cleanText = text.replace(/[*\s]+/g, ' ').trim();

              // Handle special cases - check if it contains 'foot' or 'ankle'
              const lowerText = cleanText.toLowerCase();
              if (lowerText.includes('foot') || lowerText.includes('ankle')) {
                return 'Foot & Ankle';
              }

              // Capitalize first letter of each word
              return cleanText
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
            };

            // Get unique formatted tags
            const formattedTags = post.tags.map((tag) => ({
              id: tag.id,
              text: formatTagText(tag.text)
            }));

            // Remove duplicates (e.g., if both 'foot' and 'ankle' exist)
            const uniqueTags = formattedTags.filter((tag, index, self) =>
              index === self.findIndex((t) => t.text === tag.text)
            );

            return (
              <div className="flex flex-wrap gap-1 mr-2">
                {uniqueTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-healthy-green/10 text-healthy-green border border-healthy-green/20"
                  >
                    {tag.text}
                  </span>
                ))}
              </div>
            );
          })()}

          <div className="flex-shrink-0 relative">
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {/* Dropdown Menu - iOS style */}
            <AnimatePresence>
              {showMenu && (
                <>
                  {/* Backdrop */}
                  <motion.div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                    variants={backdropFade}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  />

                  {/* Menu */}
                  <motion.div
                    className="absolute right-0 top-8 z-20 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 overflow-hidden"
                    variants={scaleIn}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                  <button
                    onClick={handleShare}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 flex items-center space-x-3 transition-colors"
                  >
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    <span>Share</span>
                  </button>

                  {user && user.id !== post.owner.id.toString() && (
                    <>
                      <button
                        onClick={handleHidePost}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                      >
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464a1.414 1.414 0 00-2-2L5.05 7.878m4.828 2L9.878 9.878m4.242 4.242L15.536 15.536a1.414 1.414 0 002 2l1.414-1.414M14.12 14.12l4.242-4.242" />
                        </svg>
                        <span>Hide Post</span>
                      </button>

                      <div className="border-t border-gray-100 my-1"></div>

                      <button
                        onClick={handleReport}
                        className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                      >
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span>Report Post</span>
                      </button>
                    </>
                  )}

                  {user && String(user.id) === String(post.owner.id) && (
                    <>
                      <div className="border-t border-gray-100 my-1"></div>
                      
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                          setShowMenu(false);
                        }}
                        disabled={deletePost.isPending}
                        className={`w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors ${
                          deletePost.isPending ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>{deletePost.isPending ? 'Deleting...' : 'Delete Post'}</span>
                      </button>
                    </>
                  )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-3">
          <h2 className="text-lg font-semibold theme-text mb-2 break-words overflow-hidden">
            <span className="line-clamp-2" title={post.title}>
              {isSearchResult ? highlightSearchTerm(post.title, searchQuery) : post.title}
            </span>
          </h2>
          <p className="theme-text whitespace-pre-wrap">
            {isSearchResult ? highlightSearchTerm(post.description, searchQuery) : post.description}
          </p>
        </div>

        <MediaGallery media={post.media} />

        {post.poll && (
          <div className="mt-3 theme-bg-secondary rounded-lg p-4">
            {post.poll.question && (
              <h3 className="text-sm font-medium theme-text-primary mb-3">
                {post.poll.question}
              </h3>
            )}
            
            {post.poll.results?.votedForOptionId && (
              <div className="mb-3 flex items-center space-x-2 text-sm font-medium text-healthy-green">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Voted!</span>
              </div>
            )}
            
            <div className="space-y-3">
              {post.poll.options.map((option) => {
                const optionVotes = post.poll?.results?.votes.find(v => v.optionId === option.id)?.count || 0;
                const totalVotes = post.poll?.results?.totalVotes || 0;
                const percentage = totalVotes > 0 ? Math.round((optionVotes / totalVotes) * 100) : 0;
                const isUserVote = post.poll?.results?.votedForOptionId === option.id;
                
                return (
                  <div key={option.id} className="relative mb-2">
                    <div className={`relative rounded-lg border overflow-hidden transition-all ${
                      isUserVote
                        ? 'border-healthy-green bg-healthy-green bg-opacity-10'
                        : 'border-gray-200 dark:border-gray-700 theme-bg-card'
                    }`}>
                      {/* Progress bar background - subtle */}
                      <div
                        className="absolute top-0 left-0 bottom-0 bg-healthy-green opacity-10 transition-all duration-500 rounded-lg"
                        style={{ width: `${percentage}%` }}
                      />

                      {/* Content */}
                      <div className="relative z-10 flex items-center justify-between w-full p-3">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            isUserVote
                              ? 'border-healthy-green bg-healthy-green'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}>
                            {isUserVote && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            isUserVote ? 'text-healthy-green dark:text-healthy-green font-semibold' : 'theme-text-primary'
                          }`}>
                            {option.text}
                          </span>
                        </div>

                        <div className="flex items-center space-x-3 flex-shrink-0">
                          {isPollCreator && (
                            <span className={`text-xs theme-text-secondary`}>
                              {optionVotes} {optionVotes === 1 ? 'vote' : 'votes'}
                            </span>
                          )}
                          <span className={`text-sm font-bold min-w-[3rem] text-right ${
                            isUserVote ? 'text-healthy-green dark:text-healthy-green' : 'theme-text-secondary'
                          }`}>
                            {percentage}%
                          </span>
                        </div>
                      </div>
                      
                      {/* Click overlay for voting - only surgeons can vote */}
                      <button
                        className={`absolute inset-0 z-20 transition-colors ${
                          isSurgeon ? 'hover:bg-black hover:bg-opacity-5 cursor-pointer' : 'cursor-not-allowed'
                        }`}
                        onClick={() => {
                          if (!isSurgeon) {
                            showInfo('Voting Disabled', 'Only surgeons can vote on polls');
                            return;
                          }

                          if (post.poll) {
                            const isChangingVote = post.poll?.results?.votedForOptionId && post.poll?.results?.votedForOptionId !== option.id;
                            const isVotingSame = post.poll?.results?.votedForOptionId === option.id;

                            if (isVotingSame) {
                              // Don't allow voting for same option again
                              return;
                            }

                            votePoll.mutate({ pollId: post.poll.id, optionId: option.id }, {
                              onSuccess: () => {
                                const message = isChangingVote ? 'Vote Changed!' : 'Vote Recorded!';
                                showSuccess(message, `You voted for "${option.text}"`);
                              }
                            });
                          }
                        }}
                        disabled={!isSurgeon || votePoll.isPending}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Poll status */}
            <div className="mt-3 flex justify-between items-center text-xs theme-text-secondary">
              <span>
                {post.poll.results?.votedForOptionId
                  ? isPollCreator
                    ? `${post.poll.results.totalVotes} total votes • Click to change vote`
                    : 'Click to change vote'
                  : 'Click to vote'
                }
              </span>
              {post.poll.results?.votedForOptionId && (
                <span className="text-healthy-green font-medium">Results</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
          <div className="flex space-x-4">
            <span>{post.commentCount} comments</span>
            {post.viewedByUser && <span className="text-healthy-green">• viewed</span>}
          </div>
        </div>

        <div className="flex items-center space-x-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <motion.button
            onClick={handleComment}
            whileHover={buttonHover}
            whileTap={buttonTap}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 dark:hover:from-cyan-900/20 dark:hover:to-teal-900/20 hover:text-trust-turquoise transition-all"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>{showComments ? 'Hide comments' : 'Comments'}</span>
          </motion.button>

          <motion.button
            onClick={handleSave}
            disabled={savePost.isPending}
            whileHover={buttonHover}
            whileTap={buttonTap}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              post.savedByUser
                ? 'text-healthy-seafoam bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 hover:from-teal-100 hover:to-cyan-100 shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-teal-50 hover:to-cyan-50 dark:hover:from-teal-900/20 dark:hover:to-cyan-900/20 hover:text-healthy-seafoam'
            }`}
          >
            <svg className="h-4 w-4" fill={post.savedByUser ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <span>Save</span>
          </motion.button>
        </div>
      </div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            className="border-t border-gray-200 dark:border-gray-700"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
          {isLoadingComments ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-healthy-green mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading comments...</p>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500">
              <p className="text-sm">Error loading comments: {error.message}</p>
            </div>
          ) : comments && comments.length > 0 ? (
            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
              {comments.map((comment) => (
                <div key={comment.id} className="flex space-x-3">
                  <div className="h-8 w-8 bg-healthy-green rounded-full flex items-center justify-center flex-shrink-0">
                    {comment.commenter.profilePicture ? (
                      <img
                        src={comment.commenter.profilePicture}
                        alt={comment.commenter.name || comment.commenter.username}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-xs font-medium select-none">
                        {(comment.commenter.name || comment.commenter.username).charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 pl-1">
                    <div className="theme-surface rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium theme-text">
                          {comment.commenter.name || comment.commenter.username}
                        </span>
                        <span className="text-xs theme-text-muted">
                          {comment.commenter.country}
                        </span>
                      </div>
                      <p className="text-sm theme-text">
                        {isSearchResult ? highlightSearchTerm(comment.comment, searchQuery) : comment.comment}
                      </p>
                      
                      {/* Comment Media Display */}
                      {comment.media && (
                        <div className="mt-2 space-y-2">
                          {comment.media?.images && comment.media.images.length > 0 && (
                            <div className="grid grid-cols-2 gap-2">
                              {comment.media.images.map((image, index) => (
                                <img
                                  key={index}
                                  src={image}
                                  alt={`Comment media ${index + 1}`}
                                  className="rounded-lg max-h-24 w-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setSelectedCommentImage({ 
                                    url: image, 
                                    images: comment.media?.images || [], 
                                    index 
                                  })}
                                />
                              ))}
                            </div>
                          )}
                          {comment.media.videos && comment.media.videos.length > 0 && (
                            <div className="space-y-2">
                              {comment.media.videos.map((video, index) => (
                                <video
                                  key={index}
                                  src={video}
                                  controls
                                  className="rounded-lg max-h-32 w-full"
                                  preload="metadata"
                                >
                                  Your browser does not support the video tag.
                                </video>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-xs theme-text-muted">
                      <span>{formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}</span>
                      {isSurgeon && (
                        <button
                          className={`flex items-center justify-center space-x-1 transition-all duration-200 p-1.5 rounded-md min-w-[50px] border ${
                            comment.upvotedByUser
                              ? 'text-healthy-green font-medium bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                              : 'hover:text-healthy-green hover:bg-gray-100 border-gray-200 dark:border-gray-700 dark:hover:bg-gray-800/40'
                          }`}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent any parent clicks
                            e.stopPropagation(); // Stop event bubbling
                            handleCommentUpvote(comment.id);
                          }}
                          disabled={upvoteCommentMutation?.isPending}
                          aria-label={comment.upvotedByUser ? "Remove upvote" : "Upvote comment"}
                          title={comment.upvotedByUser ? "Remove upvote" : "Upvote comment"}
                        >
                          <span className={`${comment.upvotedByUser ? 'transform scale-110' : ''}`}>
                            👍
                          </span>
                          <span className={comment.upvotedByUser ? 'font-medium' : ''}>
                            {comment.upvoteCount > 0 ? comment.upvoteCount : '0'}
                          </span>
                        </button>
                      )}
                      {canComment && (
                        <button
                          className="flex items-center space-x-1 hover:text-healthy-green"
                          onClick={() => handleReplyToComment(comment.id, comment.commenter.name || comment.commenter.username)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Reply</span>
                        </button>
                      )}
                      {isSurgeon && (
                        <button
                          className="flex items-center space-x-1 hover:text-red-600"
                          onClick={() => handleDeleteComment(comment.id)}
                          title="Delete comment"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Reply Input Form */}
                    {replyingTo === comment.id && canComment && (
                      <form onSubmit={handleSubmitReply} className="mt-3 ml-4 border-l-2 border-green-200 pl-3">
                        <div className="flex items-start space-x-2">
                          <div className="h-6 w-6 bg-healthy-green rounded-full flex items-center justify-center flex-shrink-0 mt-2">
                            <span className="text-white text-xs font-medium">U</span>
                          </div>
                          <div className="flex-1">
                            <div className="mb-1 flex items-center">
                              <span className="text-xs text-healthy-green font-medium">
                                Replying to {comment.commenter.name || comment.commenter.username}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-2">
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder={`Write your reply...`}
                                className="w-full theme-input p-2 rounded-md text-sm focus:ring-healthy-green focus:border-healthy-green resize-none"
                                rows={2}
                                autoFocus
                              />
                              {replyError && (
                                <div className="text-red-500 text-xs">{replyError}</div>
                              )}
                              <div className="flex justify-end space-x-2">
                                <button
                                  type="button"
                                  onClick={handleCancelReply}
                                  className="px-3 py-1.5 text-gray-600 dark:text-gray-400 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="submit"
                                  disabled={!replyText.trim() || createCommentMutation.isPending}
                                  className="px-3 py-1.5 bg-healthy-green text-white text-sm font-medium rounded-md hover:bg-fresh-green disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {createCommentMutation.isPending ? 'Posting...' : 'Reply'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    )}
                    
                    {/* Show replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="mt-3 ml-6 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                        {comment.replies.map((reply) => (
                          <div key={reply.id} className="text-sm">
                            <div className="flex items-start space-x-2">
                              <div className="h-5 w-5 bg-healthy-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                {reply.commenter.profilePicture ? (
                                  <img
                                    src={reply.commenter.profilePicture}
                                    alt={reply.commenter.name || reply.commenter.username}
                                    className="h-5 w-5 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-white text-xs font-medium">
                                    {(reply.commenter.name || reply.commenter.username).charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {reply.commenter.name || reply.commenter.username}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="text-gray-800 dark:text-gray-200 mt-1">{reply.comment}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 mt-1">
                              {isSurgeon && (
                                <button
                                  className={`flex items-center justify-center space-x-1 text-xs transition-all duration-200 p-1 rounded-md min-w-[50px] border ${
                                    reply.upvotedByUser
                                      ? 'text-healthy-green font-medium bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                      : 'text-gray-500 hover:text-healthy-green hover:bg-gray-100 border-gray-200 dark:border-gray-700 dark:hover:bg-gray-800/40'
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCommentUpvote(reply.id);
                                  }}
                                  disabled={upvoteCommentMutation.isPending}
                                >
                                  <span className={`${reply.upvotedByUser ? 'transform scale-110' : ''}`}>
                                    👍
                                  </span>
                                  <span className={reply.upvotedByUser ? 'font-medium' : ''}>
                                    {reply.upvoteCount > 0 ? reply.upvoteCount : '0'}
                                  </span>
                                </button>
                              )}
                              
                              {/* Reply to reply button */}
                              {canComment && (
                                <button
                                  className="flex items-center space-x-1 text-xs hover:text-healthy-green"
                                  onClick={() => handleReplyToComment(comment.id, reply.commenter.name || reply.commenter.username)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  <span>Reply</span>
                                </button>
                              )}
                              
                              {/* Delete reply button */}
                              {isSurgeon && (
                                <button
                                  className="flex items-center space-x-1 text-xs hover:text-red-600"
                                  onClick={() => handleDeleteComment(reply.id)}
                                  title="Delete reply"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                              )}
                            </div>
                            
                            {/* Reply form for replies */}
                            {replyingTo === comment.id && replyingToUsername === (reply.commenter.name || reply.commenter.username) && canComment && (
                              <div className="mt-2 pl-2 border-l border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-healthy-green mb-1">Replying to {reply.commenter.name || reply.commenter.username}</div>
                                <form onSubmit={handleSubmitReply} className="flex flex-col space-y-2">
                                  <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={`Write your reply...`}
                                    className="w-full theme-input p-1.5 rounded-md text-xs focus:ring-healthy-green focus:border-healthy-green resize-none"
                                    rows={2}
                                    autoFocus
                                  />
                                  <div className="flex justify-end space-x-1.5">
                                    <button
                                      type="button"
                                      onClick={handleCancelReply}
                                      className="px-2 py-1 text-gray-600 dark:text-gray-400 text-xs font-medium rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="submit"
                                      disabled={!replyText.trim() || createCommentMutation.isPending}
                                      className="px-2 py-1 bg-healthy-green text-white text-xs font-medium rounded hover:bg-fresh-green disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {createCommentMutation.isPending ? 'Posting...' : 'Reply'}
                                    </button>
                                  </div>
                                </form>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {post.commentCount > 0 ? (
                <div>
                  <p className="text-sm text-orange-600 font-medium">
                    Comments are temporarily unavailable
                  </p>
                  <p className="text-xs mt-1 text-gray-500 dark:text-gray-400 mb-3">
                    This post has {post.commentCount} comment{post.commentCount !== 1 ? 's' : ''} that couldn't be loaded
                  </p>
                  <button
                    onClick={() => refetchComments()}
                    className="text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-1 rounded-md text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Retry loading comments
                  </button>
                </div>
              ) : (
                <p className="text-sm">{canComment ? 'No comments yet. Be the first to comment!' : 'No comments yet.'}</p>
              )}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-xs mt-2 text-gray-400 space-y-1">
                  <p>Debug: commentCount={post.commentCount}, comments={comments ? comments.length : 'null'}</p>
                  <p>Post ID: {post.id}, showComments: {showComments.toString()}</p>
                  {error && <p className="text-red-400">API Error: {(error as any).message || 'Unknown error'}</p>}
                </div>
              )}
            </div>
          )}

          {/* Add Comment Form - Only for surgeons */}
          {canComment && (
            <form onSubmit={handleSubmitComment} className="p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex space-x-3">
              <div className="h-8 w-8 bg-healthy-green rounded-full flex items-center justify-center flex-shrink-0">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white text-xs font-medium select-none">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 pl-1">
                {/* Media Previews */}
                {(imagePreviews.length > 0 || videoPreviews.length > 0 || filePreviews.length > 0) && (
                  <div className="mb-2 p-2 border border-gray-200 dark:border-gray-600 rounded-md">
                    {imagePreviews.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-500 mb-1">Images:</p>
                        <div className="flex flex-wrap gap-2">
                          {imagePreviews.map((preview, index) => (
                            <div key={index} className="relative">
                              <img
                                src={preview}
                                alt={`Preview ${index + 1}`}
                                className="w-12 h-12 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {videoPreviews.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-500 mb-1">Videos:</p>
                        <div className="flex flex-wrap gap-2">
                          {videoPreviews.map((preview, index) => (
                            <div key={index} className="relative">
                              <video
                                src={preview}
                                className="w-12 h-12 object-cover rounded-md"
                                preload="metadata"
                              />
                              <button
                                type="button"
                                onClick={() => removeVideo(index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {filePreviews.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Files:</p>
                        <div className="flex flex-wrap gap-2">
                          {filePreviews.map((file, index) => (
                            <div key={index} className="relative flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[100px]">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="ml-1 text-red-500 hover:text-red-600 text-sm font-bold"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <div className="flex-1 flex items-center space-x-2">
                    {/* Comment label removed as requested */}
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment or attach media..."
                      className="flex-1 px-3 py-2 theme-input rounded-full text-sm focus:ring-healthy-green focus:border-healthy-green"
                    />
                    
                    {/* Attachment Button */}
                    <div className="relative">
                      <label className="cursor-pointer group flex items-center space-x-1 px-3 py-2 text-gray-500 hover:text-healthy-green hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" title="Attach images, videos, or PDFs">
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*,application/pdf"
                          onChange={handleAttachmentSelect}
                          className="hidden"
                        />
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="text-sm font-medium hidden sm:inline">Attach</span>
                      </label>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={(!newComment.trim() && selectedImages.length === 0 && selectedVideos.length === 0 && selectedFiles.length === 0) || createCommentMutation.isPending}
                    className="px-4 py-2 bg-healthy-green text-white text-sm font-medium rounded-full hover:bg-fresh-green disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createCommentMutation.isPending ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
            </div>
          </form>
          )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Post Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deletePost.isPending}
        danger={true}
      />
      
      {/* Delete Comment Confirmation Modal */}
      <ConfirmModal
        isOpen={showCommentDeleteConfirm}
        onClose={() => setShowCommentDeleteConfirm(false)}
        onConfirm={confirmDeleteComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteCommentMutation.isPending}
        danger={true}
      />

      {/* Comment Image Lightbox */}
      {selectedCommentImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCommentImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedCommentImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {selectedCommentImage.images.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = selectedCommentImage.index > 0 
                    ? selectedCommentImage.index - 1 
                    : selectedCommentImage.images.length - 1;
                  setSelectedCommentImage({
                    ...selectedCommentImage,
                    url: selectedCommentImage.images[newIndex],
                    index: newIndex
                  });
                }}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = selectedCommentImage.index < selectedCommentImage.images.length - 1
                    ? selectedCommentImage.index + 1 
                    : 0;
                  setSelectedCommentImage({
                    ...selectedCommentImage,
                    url: selectedCommentImage.images[newIndex],
                    index: newIndex
                  });
                }}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          <img
            src={selectedCommentImage.url}
            alt={`Comment image ${selectedCommentImage.index + 1}`}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {selectedCommentImage.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white">
              {selectedCommentImage.index + 1} / {selectedCommentImage.images.length}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}